import React, { useMemo } from "react"

import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"

import { PAYER_MAPPINGS_CONFIG } from "../../../config/payerMappings.config"
import { ButtonDropdownElement } from "../../../elements/buttonDropdown.element"
import { RuntimeHelper } from "../../../helpers/runtime.helper"
import { useHasPermissions } from "../../../hooks/hasPermissions.hook"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { payerMappingsSelector } from "../../../store/selectors/payerMappings.selector"
import { runtimeSelector } from "../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { payerMappingsExportList } from "../../../store/thunks/payerMappings.thunks"
import type { IButtonDropdownOption } from "../../../types/objects.types"

import chevronDownIcon from "@front-shared/assets/images/icons/chevron-down-dark.svg"

export const PayerMappingsCsvDropdownComponent: React.FunctionComponent<
    IPayerMappingsCsvDropdownComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const payerMappings = useAppSelector(payerMappingsSelector)
    const runtimeState = useAppSelector(runtimeSelector)

    const canManagePracticeConfigurations = useHasPermissions(
        OneVerifyPermission.managePracticeConfigurations,
        undefined,
        false,
    )

    const isExportLoading = useMemo<boolean>(() => {
        return RuntimeHelper.isPayerMappingsExportListLoading()
    }, [runtimeState.isLoading])

    const handleDownloadTemplate = () => {
        window.open(
            PAYER_MAPPINGS_CONFIG.csvTemplateUrl,
            "_blank",
            "noopener,noreferrer",
        )
    }

    const handleDownloadMappingsAsCsv = () => {
        if (!selectedPracticeRole) {
            return
        }

        dispatch(
            payerMappingsExportList({
                practice: selectedPracticeRole.practice,
                payload: undefined,
            }),
        )
    }

    const options: IButtonDropdownOption[] = [
        {
            label: "Download CSV template",
            onClick: handleDownloadTemplate,
        },

        ...(canManagePracticeConfigurations
            ? [
                  {
                      label: "Add payer mappings",
                      onClick: props.onOpenBulkUpload,
                  },
              ]
            : []),

        {
            label: "Download mappings",
            onClick: handleDownloadMappingsAsCsv,
            isDisabled: !selectedPracticeRole,
        },
    ]

    return (
        <ButtonDropdownElement
            buttonLabel={`CSV${
                isExportLoading ? ` (${payerMappings.exportProgress}%)` : "..."
            }`}
            buttonType="default"
            buttonIcon={chevronDownIcon}
            buttonIsRightIcon
            buttonIsLoading={isExportLoading}
            dropdownClassName="min-w-[240px]"
            buttonClassName="
                rounded-[22px] px-[16px] py-[13px]
                bg-brand-warmLight border border-solid
                border-brand-primary hover:bg-brand-lilacLight
            "
            options={options}
        />
    )
}

interface IPayerMappingsCsvDropdownComponentProps {
    onOpenBulkUpload: () => void
}
